// 工程校核检查：基于计算结果给出提示/阻断

/**
 * 运行工程校核检查
 * @param {Object} values - 输入参数
 * @param {Object} results - 计算结果
 * @returns {{ warnings: string[], blockers: string[] }}
 */
export const runEngineeringChecks = (values, results) => {
  const warnings = [];
  const blockers = [];

  // 灵敏系数阈值（典型建议 ≥ 2.0，<1.2 视为严重）
  const sensitivity = results?.sensitivity;
  if (typeof sensitivity === 'number') {
    if (sensitivity < 1.2) {
      blockers.push('灵敏系数 Ksen < 1.2，可能拒动，请提高启动电流或核对故障电流');
    } else if (sensitivity < 2.0) {
      warnings.push('灵敏系数 Ksen < 2.0，建议提高（典型≥2）');
    }
  }

  // 反时限曲线不动作（M≤1 → Infinity）
  if (results?.operateTime === Infinity) {
    warnings.push('故障倍数 M ≤ 1，反时限曲线不动作；请校核启动电流与故障电流二次值');
  }

  // 动作时间过短提醒（可能影响与上级的选择性裕度）
  if (typeof results?.operateTime === 'number' && isFinite(results.operateTime)) {
    if (results.operateTime < 0.1) {
      warnings.push('动作时间较短（<0.10s），可能影响与上级选择性，请校核时间裕度');
    }
  }

  // 方向保护：极化角典型范围提醒
  if (values?.protectionType === 'directional' && typeof values?.polarizingAngle === 'number') {
    if (values.polarizingAngle < 20 || values.polarizingAngle > 60) {
      warnings.push('极化角超出典型范围（20°~60°），请结合潮流/接线方式校核');
    }
  }

  // 零序：k0典型范围提醒
  if (values?.protectionType === 'zero_sequence' && typeof values?.k0 === 'number') {
    if (values.k0 < 0.2 || values.k0 > 0.5) {
      warnings.push('k0 超出典型范围（0.2~0.5），建议结合接地方式与CT接线校核');
    }
  }

  // 过电流：启动电流与额定电流的系数校核（一次侧比例系数 1.2~1.5）
  if (values?.protectionType === 'overcurrent' && typeof results?.pickupCurrent === 'number') {
    const nCT = values?.ctRatio || 1;
    const irated = values?.ratedCurrent || 0;
    const k = nCT > 0 ? (results.pickupCurrent * nCT) / (irated || 1) : null; // 一次侧系数
    if (typeof k === 'number') {
      if (k < 1.1 || k > 1.6) {
        warnings.push('启动电流一次侧系数偏离典型范围（1.2~1.5），请结合负荷电流校核');
      }
    }
  }

  // CT 饱和简化提醒：当二次电流显著偏高（>10A）时提示可能饱和或误差增大
  if (['overcurrent', 'directional', 'zero_sequence', 'longitudinal'].includes(values?.protectionType)) {
    if (typeof results?.secondaryCurrent === 'number' && results.secondaryCurrent > 10) {
      warnings.push('CT 二次电流较大（>10A），可能导致 CT 饱和或误差增大');
    }
    if (typeof results?.zeroSequenceCurrent === 'number' && results.zeroSequenceCurrent > 10) {
      warnings.push('零序二次电流较大（>10A），可能导致 CT 饱和或误差增大');
    }
    if (typeof results?.differentialCurrent === 'number' && results.differentialCurrent > 10) {
      warnings.push('差动二次电流较大（>10A），可能导致 CT 饱和或误差增大');
    }
  }

  // 距离保护：分段阻抗关系校核（Zone2 > Zone1，Zone3 > Zone2）
  if (values?.protectionType === 'distance') {
    const z1 = results?.zone1Setting;
    const z2 = results?.zone2Setting;
    const z3 = results?.zone3Setting;
    if (typeof z1 === 'number' && typeof z2 === 'number' && z2 <= z1) {
      warnings.push('距离保护分段顺序异常：Zone2 ≤ Zone1（应满足 Zone2 > Zone1）');
    }
    if (typeof z2 === 'number' && typeof z3 === 'number' && z3 <= z2) {
      warnings.push('距离保护分段顺序异常：Zone3 ≤ Zone2（应满足 Zone3 > Zone2）');
    }
  }

  // 差动保护：二次谐波制动系数典型范围提醒（0.10~0.30）
  if (values?.protectionType === 'differential' && typeof results?.secondHarmonicBlocking === 'number') {
    const kb = results.secondHarmonicBlocking;
    if (kb < 0.10 || kb > 0.30) {
      warnings.push('二次谐波制动系数超出典型范围（0.10~0.30），请校核投运与励磁涌流条件');
    }
  }

  // 差动保护：分段有效性与顺序校核（Id–Ir 特性）
  if (values?.protectionType === 'differential') {
    const characteristic = results?.characteristic || {};
    const segments = Array.isArray(characteristic?.segments) ? characteristic.segments : [];
    const diffPickup = Number(characteristic?.diffPickup);
    if (segments.length > 0) {
      // 逐段有效性
      segments.forEach((seg, idx) => {
        const k = Number(seg?.slope);
        const th = seg?.threshold;
        if (!Number.isFinite(k) || k < 0) {
          warnings.push(`差动分段 #${idx + 1} 斜率K无效（需为≥0的有限数值）`);
        }
        const thNum = Number(th);
        const thValid = (Number.isFinite(thNum) && thNum >= 0) || th === Infinity;
        if (!thValid) {
          warnings.push(`差动分段 #${idx + 1} 阈值 Ir 无效（需≥0或∞）`);
        }
      });

      // 阈值递增（允许最后一段为 ∞）
      for (let i = 0; i < segments.length - 1; i++) {
        const a = segments[i]?.threshold;
        const b = segments[i + 1]?.threshold;
        const av = Number.isFinite(Number(a)) ? Number(a) : Infinity;
        const bv = Number.isFinite(Number(b)) ? Number(b) : Infinity;
        if (!(bv > av)) {
          warnings.push('差动分段阈值非递增（应按 Ir 递增排列）');
          break;
        }
      }

      // 起始段建议：K=0 至 Ir=diffPickup（保证连续性与抗误动）
      if (Number.isFinite(diffPickup) && diffPickup > 0) {
        const first = segments[0] || {};
        const tol = 1e-6;
        if (!(Number(first?.slope) === 0 && Math.abs(Number(first?.threshold) - diffPickup) <= tol)) {
          warnings.push('差动起始段建议为 K=0 且阈值 Ir=diffPickup，以确保特性连续与抗误动');
        }
      }

      // 末段建议：阈值为 ∞（覆盖高制动电流范围）
      const last = segments[segments.length - 1] || {};
      if (last?.threshold !== Infinity) {
        warnings.push('差动最后一段未延伸至无穷大（∞），建议延伸以覆盖高制动电流');
      }

      // 备注轻度校核：空白或过长（不阻断，仅提示导出可读性）
      const NOTE_MAX_LEN = 120;
      segments.forEach((seg, idx) => {
        const note = (seg?.note ?? '').trim();
        if (seg?.note !== undefined) {
          if (note.length === 0) {
            warnings.push(`【可读性】差动分段 #${idx + 1} 备注为空白，建议填写简短说明以便导出报告`);
          } else if (note.length > NOTE_MAX_LEN) {
            warnings.push(`【可读性】差动分段 #${idx + 1} 备注较长（>${NOTE_MAX_LEN}字符），导出报告可能拥挤，建议精简`);
          }
        }
      });
    }
  }

  return { warnings, blockers };
};